import API from '@aws-amplify/api';
import { backOff } from 'exponential-backoff';
import { PortalInfoSchema } from '../constants/PortalInfoSchema';
import { ApiSurcharge, PricingAdjustmentConditionOption } from '../types/PricingAdjustment';
import { Configurator } from '../types/Configurator';
import { Dealer } from '../types/Dealer';
import { OrderRequest } from '../types/OrderRequest';
import { OrderResponse } from '../types/OrderResponse';
import { SalesRep } from '../types/SalesRep';
import { Vendor, VendorData } from '../types/VendorData';
import { WhatsNew } from '../types/WhatsNew';
import { GroupSettings } from '../types/Group';
import { defaultI18nNamespace } from '../constants/I18n';
import { SceneEnvironment } from '../types/SceneEnvironment';
import { S3Buckets } from '../constants/S3';
import { Environment } from '../constants/Environment';
import { API_NAMES } from '../constants/App';

class PortalService {
  apiName = API_NAMES.PORTAL;

  /**
   * Gets the current user information.
   *
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getCurrentUserData(groupId: string, forceRefetch = false): Promise<any> {
    return API.get(this.apiName, `/users/me/data/${groupId}?forceRefetch=${forceRefetch}`, {});
  }

  /**
   * Gets the current users groups.
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getCurrentUsersGroups(): Promise<any> {
    return API.get(this.apiName, `/users/me/groups`, {});
  }

  /**
   * Gets the current users configurators.
   */
  async getConfigurators(groupId: string): Promise<any> {
    return API.get(this.apiName, `/users/me/configurators/${groupId}`, {});
  }

  /**
   * Gets user preferences
   *
   * @param {string} username
   */
  async getUserPreferences(): Promise<any> {
    return API.get(this.apiName, `/users/me/preferences`, {});
  }

  /**
   * Adds or updates user preferences
   *
   * @param {string} username
   */
  async saveUserPreferences(preferences: any): Promise<any> {
    return API.post(this.apiName, `/users/me/preferences`, {
      body: {
        preferences,
      },
    });
  }

  /**
   * Adds an event for the current user.
   *
   * @param {string} groupId
   * @param {string | undefined} clientId
   * @param {string | undefined} eventCategory
   * @param {string} eventType
   */
  async addUserEvent(
    groupId: string,
    clientId: string | undefined,
    eventCategory: string | undefined,
    eventType: string,
  ): Promise<any> {
    return API.post(this.apiName, `/users/me/events`, {
      body: {
        groupId,
        clientId,
        eventCategory,
        eventType,
      },
    });
  }

  /**
   * Validates the given username.
   * Checks for:
   * - Existence in current group
   * - Existence in current group but another dealer
   * - Existence in another group
   *
   * @param {string} username
   * @param {string} groupId
   * @param {string} dealer
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async validateUser(username: string, groupId: string, dealer?: string): Promise<{ message: string }> {
    return API.post(this.apiName, `/users/validate`, { body: { username, groupId, dealer } });
  }

  /**
   * Deletes the specified user.
   *
   * @param {string} username
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async deleteUser(username: string, groupId: string): Promise<any> {
    return API.del(this.apiName, `/users/${groupId}/${username}`, {});
  }

  /**
   * Sends an invitation to the specified user.
   *
   * @param {string} username
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async sendNewUserInvitation(username: string, groupId: string): Promise<any> {
    return API.patch(this.apiName, `/users/${groupId}/send-invitation/${username}`, {});
  }

  /**
   * Resends the invitation to the specified user.
   *
   * @param {string} username
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async resendUserInvitation(username: string, groupId: string): Promise<any> {
    return API.patch(this.apiName, `/users/${groupId}/resend-invitation/${username}`, {});
  }

  /**
   * Resets the password of the specified user.
   *
   * @param {string} username
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async resetUserPassword(username: string, groupId: string): Promise<any> {
    return API.patch(this.apiName, `/users/${groupId}/reset-password/${username}`, {});
  }

  /**
   * Initiates the impersonation of the specified user.
   *
   * @param {string} username
   * @param {string} usernameToImpersonate
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async initiateImpersonateUser(username: string, usernameToImpersonate: string): Promise<any> {
    return API.post(this.apiName, `/users/initiate-impersonate`, { body: { username, usernameToImpersonate } });
  }

  /**
   * Validates the impersonation of the specified user.
   *
   * @param {string} code
   * @param {string} username
   * @param {string} session
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async validateImpersonation(code: string, username: string, session: any): Promise<any> {
    return API.post(this.apiName, `/users/validate-impersonate`, { body: { code, username, session } });
  }

  /**
   * Gets a group by its groupId.
   *
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getGroup(groupId: string): Promise<any> {
    return API.get(this.apiName, `/groups/${groupId}`, {});
  }

  /**
   * Lists groups.
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getGroups(): Promise<any> {
    return API.get(this.apiName, '/groups', {});
  }

  /**
   * Creates a group and returns its groupId
   *
   * @param {string} groupName
   * @param {Environment} environment For production only, 'staging' is valid, otherwise defaults to current environment
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async createGroup(groupName: string, environment?: Environment): Promise<any> {
    return API.post(this.apiName, '/groups', { body: { groupName, environment } });
  }

  /**
   * Deletes the specified group.
   *
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async deleteGroup(groupId: string): Promise<any> {
    return API.del(this.apiName, `/groups/${groupId}`, {});
  }

  /**
   * Updates the specified group.
   *
   * @param {string} groupId
   * @param {string} group
   * @param {Environment} environment For production only, 'staging' is valid, otherwise defaults to current environment
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateGroup(groupId: string, group: any, environment?: Environment): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}`, {
      body: { group, environment },
    });
  }

  /**
   * Adds the specified configurator to the specified group.
   *
   * @param {string} groupId
   * @param {Configurator} configurator
   * @param {Environment} environment For production only, 'staging' is valid, otherwise defaults to current environment
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addConfiguratorToGroup(groupId: string, configurator: Configurator, environment?: Environment): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}/configurators`, {
      body: { action: 'ADD', configurator, environment },
    });
  }

  /**
   * Saves a configurator to the specified group.
   *
   * @param {string} groupId
   * @param {Configurator} configurator
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async saveConfigurator(groupId: string, configurator: Configurator): Promise<any> {
    return API.put(this.apiName, `/groups/${groupId}/configurators`, {
      body: { configurator },
    });
  }

  /**
   * Removes the specified configurator to the specified group.
   *
   * @param {string} groupId
   * @param {Configurator} configurator
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async removeConfiguratorFromGroup(groupId: string, configurator: Configurator): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}/configurators`, {
      body: { action: 'REMOVE', configurator },
    });
  }

  /**
   * Adds the specified member to the specified group.
   *
   * @param {string} groupId
   * @param {string} username
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} phone
   * @param {string[]} memberPermissions
   * @param {string} memberdealers
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addMemberToGroup(
    groupId: string,
    username: string,
    firstName: string,
    lastName: string,
    phone: string,
    memberPermissions: string[],
    memberdealers: string[],
  ): Promise<any> {
    return API.post(this.apiName, `/groups/${groupId}/members`, {
      body: { username, firstName, lastName, phone, permissions: memberPermissions, dealers: memberdealers },
    });
  }

  /**
   * Adds the specified members to the specified group.
   *
   * @param {string} groupId
   * @param {string} usernames
   * @param {string[]} memberPermissions
   * @param {string} memberdealers
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addBulkMemberToGroup(
    groupId: string,
    usernames: string,
    memberPermissions: string[],
    memberdealers: string[],
  ): Promise<any> {
    return API.post(this.apiName, `/groups/${groupId}/bulk-members`, {
      body: { usernames, permissions: memberPermissions, dealers: memberdealers },
    });
  }

  /**
   * Updates the specified member of the specified group.
   *
   * @param {string} groupId
   * @param {string} username
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} phone
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateMemberOfGroup(
    groupId: string,
    username: string,
    firstName: string,
    lastName: string,
    phone: string,
  ): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}/members/${username}/attributes`, {
      body: { firstName, lastName, phone },
    });
  }

  /**
   * Updates the member permissions for to the specified group.
   *
   * @param {string} groupId
   * @param {string} username
   * @param {string[]} permissions
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateGroupMemberPermissions(groupId: string, username: string, permissions: string[]): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}/members/${username}/permissions`, { body: { permissions } });
  }

  /**
   * Updates the member permissions for to the specified group.
   *
   * @param {string} groupId
   * @param {string} username
   * @param {string} dealers
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateGroupMemberDealers(groupId: string, username: string, dealers: string[]): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}/members/${username}/dealers`, { body: { dealers } });
  }

  /**
   * Updates the group settings for to the specified group.
   *
   * @param {string} groupId
   * @param {string} username
   * @param {string} dealers
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateGroupSettings(groupId: string, settings: GroupSettings): Promise<any> {
    return API.patch(this.apiName, `/groups/${groupId}/settings`, { body: { settings } });
  }

  /**
   * Get orders
   *
   * @param {OrderRequest} orderRequest
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getOrders(orderRequest: OrderRequest): Promise<OrderResponse> {
    return API.get(this.apiName, '/orders', orderRequest.options);
  }

  /**
   * Get orders as a csv Blob
   *
   * @param {OrderRequest} orderRequest
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  // eslint-disable-next-line class-methods-use-this
  async getOrdersCsv(orderRequest: OrderRequest): Promise<Blob> {
    return API.get(this.apiName, `/orders/csv`, {
      ...orderRequest.options,
      responseType: 'blob',
    });
  }

  /**
   * Sets or updates a value for a JSON key in portalInfo. Providing 'null' or undefined is the same as
   * removing the value from the JSON document.
   *
   * @param {string} clientId
   * @param {string} uuid
   * @param {PortalInfoSchema} key
   * @param {any} value
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async setPortalInfoItem(clientId: string, uuid: string, key: PortalInfoSchema, value?: any): Promise<any> {
    return API.post(this.apiName, `/order/portalinfo`, {
      body: {
        clientId,
        uuid,
        portalInfo: { [key]: value || null },
      },
    });
  }

  /**
   * Sends an email to a dealer or salesrep when a lead is assigned to them.
   * The email is sent using the sesService.
   *
   * @param {string} clientId
   * @param {string} uuid
   * @param {string} emailAddress
   * @param {string} status
   * @param {string} site
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async sendLeadAssignementEmail(
    clientId: string,
    uuid: string,
    emailAddress: string,
    status: string,
    site: string,
    logoUrl: string,
    buttonColor: string,
  ): Promise<any> {
    return API.post(this.apiName, `/order/assign`, {
      body: {
        clientId,
        uuid,
        emailAddress,
        status,
        site,
        logoUrl,
        buttonColor,
      },
    });
  }

  /**
   * Gets the latest what's new records stored in the repository for a clientId.
   *
   * @param {string} clientId
   * @param {number} count - number of records to return (default: 1)
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getLatestWhatsNew(clientId: string, count = 1): Promise<{ whatsNew: WhatsNew[] }> {
    return API.get(this.apiName, `/settings/whats-new/latest/${clientId}/${count}`, {});
  }

  /**
   * Gets a list of all the what's new stored in the repository for a clientId.
   *
   * @param {string} clientId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getWhatsNew(clientId: string): Promise<{ whatsNew: WhatsNew[] }> {
    return API.get(this.apiName, `/settings/whats-new/${clientId}`, {});
  }

  /**
   * Adds an array of what's new to the repository for a clientId.
   *
   * @param {string} clientId
   * @param {WhatsNew[]} whatsNew
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addWhatsNew(clientId: string, whatsNew: WhatsNew[]): Promise<any> {
    return API.post(this.apiName, `/settings/whats-new/${clientId}`, {
      body: {
        whatsNew,
      },
    });
  }

  /**
   * Saves a what's new to the repository.
   *
   * @param {string} clientId
   * @param {string} id
   * @param {WhatsNew} whatsNew
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateWhatsNew(clientId: string, id: string, whatsNew: WhatsNew): Promise<any> {
    return API.put(this.apiName, `/settings/whats-new/${clientId}/${id}`, {
      body: {
        whatsNew,
      },
    });
  }

  /**
   * Removes a what's new stored in the repository for a clientId
   *
   * @param {string} clientId
   * @param {string} id
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async removeWhatsNew(clientId: string, id: string): Promise<any> {
    return API.del(this.apiName, `/settings/whats-new/${clientId}/${id}`, {});
  }

  /**
   * Gets a list of all the Scene Environments stored in the repository for a clientId.
   *
   * @param {string} clientId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getSceneEnvironment(clientId: string): Promise<{ sceneEnvironment: SceneEnvironment[] }> {
    return API.get(this.apiName, `/settings/scene-environment/${clientId}`, {});
  }

  /**
   * Adds an array of Scene Enviroments to the repository for a clientId.
   *
   * @param {string} clientId
   * @param {SceneEnvironment[]} sceneEnvironment
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addSceneEnvironment(clientId: string, sceneEnvironment: SceneEnvironment[]): Promise<any> {
    return API.post(this.apiName, `/settings/scene-environment/${clientId}`, {
      body: {
        sceneEnvironment,
      },
    });
  }

  /**
   * Saves a Scene Enviroment to the repository.
   *
   * @param {string} clientId
   * @param {string} key
   * @param {SceneEnvironment} sceneEnvironment
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async updateSceneEnvironment(clientId: string, key: string, sceneEnvironment: SceneEnvironment): Promise<any> {
    return API.put(this.apiName, `/settings/scene-environment/${clientId}/${key}`, {
      body: {
        sceneEnvironment,
      },
    });
  }

  /**
   * Removes a Scene Enviroment stored in the repository for a clientId
   *
   * @param {string} clientId
   * @param {string} key
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async removeSceneEnvironment(clientId: string, key: string): Promise<any> {
    return API.del(this.apiName, `/settings/scene-environment/${clientId}/${key}`, {});
  }

  /**
   * Gets a list of all the dealers stored in the dealer repository for all clientIds.
   *
   * @param {string[]} clientIds
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getAllDealers(clientIds: string[], groupId: string): Promise<{ dealers: Dealer[] }> {
    return API.post(this.apiName, `/dealers`, {
      body: { clientIds, groupId },
    });
  }

  /**
   * Gets a list of all the dealers stored in the dealer repository for a clientId.
   *
   * @param {string} clientId
   * @param {string} groupId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getDealers(clientId: string, groupId: string): Promise<{ dealers: Dealer[] }> {
    return API.get(this.apiName, `/dealers/${clientId}/group/${groupId}`, {});
  }

  /**
   * Adds an array of dealers to the dealer repository for a clientId.
   *
   * @param {string} clientId
   * @param {Dealer[]} dealers
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addDealers(clientId: string, dealers: Dealer[]): Promise<any> {
    return API.post(this.apiName, `/dealers/${clientId}`, {
      body: {
        dealers,
      },
    });
  }

  /**
   * Saves a dealer to the dealer repository.
   *
   * @param {string} clientId
   * @param {string} id
   * @param {Dealer} dealer
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async saveDealer(clientId: string, id: number | undefined, dealer: Dealer): Promise<any> {
    return API.put(this.apiName, `/dealers/${clientId}/${id}`, {
      body: {
        dealer,
      },
    });
  }

  /**
   * Removes a dealer stored in the dealer repository for a clientId
   *
   * @param {string} clientId
   * @param {string} id
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async removeDealer(clientId: string, id: number | undefined): Promise<any> {
    return API.del(this.apiName, `/dealers/${clientId}/${id}`, {});
  }

  /**
   * Gets basic configuration information about a vendor.
   *
   * @param {string} configurator
   * @param {string} vendor
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getVendor(configurator: string, vendor: string): Promise<Vendor> {
    return API.get(this.apiName, `/vendor/${configurator}/${vendor}`, {});
  }

  /**
   * Gets all dealers defined in a configurator for a vendor.
   *
   * @param {{ configurator: string; vendor: string }[]} configVendors
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getAllDealersFromVendor(configVendors: { configurator: string; vendor: string }[]): Promise<any> {
    return API.post(this.apiName, `/vendor/dealers`, {
      body: { configVendors },
    });
  }

  /**
   * Gets all dealers defined in a configurator for a vendor.
   *
   * @param {string} configurator
   * @param {string} vendorKey
   * @param {string} supplierKey
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getDealersFromVendor(configurator: string, vendorKey: string, supplierKey: string): Promise<any> {
    return API.get(this.apiName, `/vendor/${configurator}/${vendorKey}/${supplierKey}/dealers`, {});
  }

  /**
   * Gets the pricing conditions stored in the pricing repository for a clientId.
   *
   * @param {string} clientId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getConditions(clientId: string): Promise<{ conditions: PricingAdjustmentConditionOption[] }> {
    return API.get(this.apiName, `/pricing/condition/${clientId}`, {});
  }

  /**
   * Gets the active surcharge stored in the pricing repository for a clientId.
   *
   * @param {string} clientId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getSurcharge(clientId: string): Promise<{ surcharge: ApiSurcharge[] }> {
    return API.get(this.apiName, `/pricing/surcharge/${clientId}`, {});
  }

  /**
   * Gets the active surcharge stored in the pricing repository for a clientId.
   *
   * @param {string} clientId
   * @param {string} status
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getSurchargeByStatus(clientId: string, status: string): Promise<{ surcharge: ApiSurcharge[] }> {
    return API.get(this.apiName, `/pricing/surcharge/status/${clientId}/${status}`, {});
  }

  /**
   * Adds an array of surcharges to the pricing repository for a clientId.
   *
   * @param {string} clientId
   * @param {ApiSurcharge[]} surcharges
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async addSurcharge(clientId: string, surcharge: ApiSurcharge[]): Promise<any> {
    return API.post(this.apiName, `/pricing/surcharge/${clientId}`, {
      body: {
        surcharge,
      },
    });
  }

  /**
   * Saves a surcharge to the pricing repository.
   *
   * @param {string} clientId
   * @param {string} key
   * @param {ApiSurcharge} surcharge
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async saveSurcharge(clientId: string, key: string, surcharge: ApiSurcharge): Promise<any> {
    return API.put(this.apiName, `/pricing/surcharge/${clientId}/${key}`, {
      body: {
        surcharge,
      },
    });
  }

  /**
   * Removes a surcharge stored in the pricing repository for a clientId
   *
   * @param {string} clientId
   * @param {string} key
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async removeSurcharge(clientId: string, key: string): Promise<any> {
    return API.del(this.apiName, `/pricing/surcharge/${clientId}/${key}`, {});
  }

  /**
   * Get verified quote hashes for a clientId
   *
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getVerifiedQuotesMgmtLink(): Promise<{ link: string } | { message: string }> {
    return API.get(this.apiName, `/pricing/verify/getlink`, {});
  }

  /**
   * Get verified quote hashes for a clientId
   *
   * @param {string} clientId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getVerifiedQuotes(clientId: string): Promise<{ clientId: string; quoteList: string[] } | { error: string }> {
    return API.get(this.apiName, `/pricing/verify/getquotelist/${clientId}`, {});
  }

  /**
   * Run a single quote verification for a client
   *
   * @param {string} clientId
   * @param {string} hash
   * @param {string} clientBranch
   * @param {string} structureBranch
   * @param {string} systemBranch
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async runVerifiedQuote(
    clientId: string,
    hash: string,
    requestorEmail: string,
    clientBranch: string,
    structureBranch: string,
    systemBranch: string,
  ): Promise<
    | {
        priceDiffDetected: boolean;
        newQuoteDetected: boolean;
        priceDiffAmt: number;
      }
    | {
        invalidConfigurationDetected: true;
        stateChanges: Record<string, unknown>[];
      }
    | { message: string } // Error case
  > {
    // This endpoint calls another endpoint that is known to timeout _occasionally_ - https://app.shortcut.com/idearoominc/story/40643
    // As of this writing this will retry the request a couple extra times, which appears to resolve these occasional timeouts.
    return backOff(
      () =>
        API.post(this.apiName, `/pricing/verify/singlequote`, {
          body: {
            clientId,
            hash,
            requestorEmail,
            clientBranch,
            structureBranch,
            systemBranch,
          },
        }),
      {
        // number of total attempts before we actually hand off error to consumer code.
        numOfAttempts: 5,
      },
    );
  }

  /**
   * Adds an array of sales reps to the sales rep repository for a clientId.
   *
   * @param {string} clientId
   * @param {SalesRep[]} salesReps
   * @param {Environment} environment For production only, 'staging' is valid, otherwise defaults to current environment
   */
  async addSalesReps(clientId: string, salesReps: SalesRep[], environment?: Environment): Promise<any> {
    return API.post(this.apiName, `/salesreps/${clientId}`, {
      body: {
        salesReps,
        environment,
      },
    });
  }

  /**
   * Saves a sales rep to the sales rep repository.
   *
   * @param clientId
   * @param email
   * @param salesRep
   */
  async saveSalesRep(clientId: string, salesRep: SalesRep): Promise<any> {
    return API.put(this.apiName, `/salesreps/${clientId}/${salesRep.email}`, {
      body: {
        salesRep,
      },
    });
  }

  /**
   * Removes a sales rep stored in the sales rep repository for a clientId
   *
   * @param clientId
   * @param email
   */
  async removeSalesRep(clientId: string, email: string): Promise<any> {
    return API.del(this.apiName, `/salesreps/${clientId}/${email}`, {});
  }

  /**
   * Gets all suppliers for a given clientId
   *
   * @param {string} clientId
   * @return {Promise} A promise that resolves to an object with response status and JSON data, if successful.
   */
  async getSuppliers(clientId: string): Promise<any> {
    return API.get(this.apiName, `/vendorData/${clientId}/suppliers`, {});
  }

  /**
   * Adds a vendorData record to the vendor data repository for a clientId
   *
   * @param clientId
   * @param vendorData
   * @return {Promise}
   */
  async addVendorData(clientId: string, vendorData: VendorData): Promise<any> {
    return API.post(this.apiName, `/vendorData/${clientId}`, {
      body: {
        vendorData,
      },
    });
  }

  /**
   * Saves vendorData to the vendor data repository
   *
   * @param clientId
   * @param vendorData
   * @return {Promise}
   */
  async saveVendorData(clientId: string, vendorData: VendorData, environment?: Environment): Promise<any> {
    return API.put(this.apiName, `/vendorData/${clientId}`, {
      body: {
        vendorData,
        environment,
      },
    });
  }

  /**
   * Create a CloudFront distribution
   *
   * @param {string} domain
   */
  async createDistribution(domain: string): Promise<any> {
    return API.post(this.apiName, `/configurator/distribution/${domain}`, {});
  }

  /**
   * Request a certificate from ACM
   *
   * @param {string} domain
   */
  async requestCertificate(domain: string): Promise<any> {
    return API.post(this.apiName, `/configurator/certificate/${domain}`, {});
  }

  /**
   * Gets configurator info for a clientId
   *
   * @param {string} clientId
   */
  async getConfiguratorInfo(clientId: string): Promise<any> {
    return API.get(this.apiName, `/configurator/info/${clientId}`, {});
  }

  /**
   * Adds or updates configurator info for a clientId
   *
   * @param {string} clientId
   * @param {string} attributes
   */
  async addConfiguratorInfo(clientId: string, attributes: any): Promise<any> {
    return API.post(this.apiName, `/configurator/info/${clientId}`, {
      body: {
        attributes,
      },
    });
  }

  /**
   * Checks to see if the given provider is valid
   *
   * @param provider
   */
  async getValidSsoProvider(provider: string): Promise<{ validProvider: string }> {
    return API.get(this.apiName, `/sso/validProvider/${provider}`, {});
  }

  /**
   * Gets the i18n SalesView data for a given language code
   * @param language
   * @returns {Promise}
   */
  async getSalesViewI18nData(language: string): Promise<any> {
    return API.get(this.apiName, `/i18n/${S3Buckets.IdeaRoomI18n}/${language}/${defaultI18nNamespace}.json`, {});
  }

  /**
   * Gets a JWT token for the given client ID and Metabase dashboard ID
   * @param clientId
   * @param dashboardId
   * @returns {Promise}
   */
  async getMetabaseToken(clientId: string, dashboardId: string): Promise<any> {
    return API.get(this.apiName, `/usage/token/${dashboardId}/${clientId}`, {});
  }
}

export const portalService = new PortalService();
